import { useCallback, useEffect, useState } from "react";
import { apiFetch } from "../../../context/AuthContext";

// Source for the "Assign to" picker — reuses the SAME hierarchy endpoint
// (Manager)employeemanagement already relies on
// (`GET /employees/clients/:clientName/hierarchy?managerInfinityCode=...`),
// but only takes the first level of the returned tree (this manager's
// DIRECT reports), not the fully flattened multi-level hierarchy that page
// builds for its own table. No new backend endpoint — see
// WEB_TASK_ASSIGNMENT_UI_PLAN.md §5.
const useDirectReports = (BASE_URL, clientName, currentInfinityCode, language) => {
  const [directReports, setDirectReports] = useState([]);
  const [loading, setLoading] = useState(false);

  const fetchDirectReports = useCallback(async () => {
    if (!clientName || !currentInfinityCode || clientName.toLowerCase() === "infinity") {
      // The "Infinity" umbrella tenant has no single hierarchy root to
      // resolve a manager's own direct reports from — treated as "no
      // direct reports" here rather than fetching the entire company.
      setDirectReports([]);
      return;
    }
    setLoading(true);
    try {
      const response = await apiFetch(
        `${BASE_URL}/employees/clients/${clientName}/hierarchy?managerInfinityCode=${currentInfinityCode}`,
        { method: "GET" }
      );
      const json = await response.json();
      const root = json?.hierarchy?.[0];
      const children = root?.children || [];
      // Same name-resolution convention used everywhere else in this
      // codebase (e.g. (Manager)employeemanagement's employeeMapper.js):
      // `sr` is the English name field, `arabicName` is the Arabic one —
      // pick based on the app's current language, not a fixed fallback
      // order, so this shows English names when the UI is English and vice
      // versa instead of always showing whichever field happened to exist.
      setDirectReports(
        children.map((emp) => ({
          infinityCode: emp.infinityCode,
          name:
            language === "ar"
              ? emp.arabicName || emp.sr || emp.infinityCode
              : emp.sr || emp.arabicName || emp.infinityCode,
        }))
      );
    } catch (error) {
      console.error("Error fetching direct reports:", error);
      setDirectReports([]);
    } finally {
      setLoading(false);
    }
  }, [BASE_URL, clientName, currentInfinityCode, language]);

  useEffect(() => {
    fetchDirectReports();
  }, [fetchDirectReports]);

  return { directReports, loading, refetch: fetchDirectReports };
};

export default useDirectReports;
